import { useAuth } from "@clerk/clerk-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  SocialPlatform,
  UpdateBookingLinkRequest,
  UpdateEmailConnectionRequest,
  UpdateTwilioSettingsRequest,
} from "@/lib/api-client";
import {
  ApiError,
  connectEmailInbox,
  connectIntegration,
  disconnectEmailInbox,
  disconnectIntegration,
  fetchBookingLink,
  fetchEmailConnection,
  fetchIntegrations,
  fetchTwilioSettings,
  updateBookingLink,
  updateEmailConnection,
  updateTwilioSettings,
} from "@/lib/api-client";

const settingsKeys = {
  twilio: () => ["settings", "twilio"] as const,
  integrations: () => ["settings", "integrations"] as const,
  booking: () => ["settings", "booking"] as const,
  emailConnection: () => ["settings", "email-connection"] as const,
};

export function useTwilioSettings() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: settingsKeys.twilio(),
    queryFn: () => fetchTwilioSettings({ getToken }),
  });
}

export function useUpdateTwilioSettings() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateTwilioSettingsRequest) => updateTwilioSettings(body, { getToken }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.twilio() });
    },
  });
}

export function useBookingLink() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: settingsKeys.booking(),
    queryFn: () => fetchBookingLink({ getToken }),
  });
}

export function useUpdateBookingLink() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateBookingLinkRequest) => updateBookingLink(body, { getToken }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.booking() });
    },
  });
}

export function useIntegrations() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: settingsKeys.integrations(),
    queryFn: () => fetchIntegrations({ getToken }),
  });
}

export function useConnectIntegration() {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: (platform: SocialPlatform) => connectIntegration(platform, { getToken }),
  });
}

export function useDisconnectIntegration() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (platform: SocialPlatform) => disconnectIntegration(platform, { getToken }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.integrations() });
    },
  });
}

// ── Email lead ingest (M-EML) ─────────────────────────────────────────────────

export function useEmailConnection() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: settingsKeys.emailConnection(),
    queryFn: () => fetchEmailConnection({ getToken }),
    retry: (count, err) => {
      // 404 = no connection configured yet — not an error worth retrying
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 2;
    },
  });
}

export function useConnectEmailInbox() {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: () => connectEmailInbox({ getToken }),
  });
}

export function useUpdateEmailConnection() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateEmailConnectionRequest) => updateEmailConnection(body, { getToken }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.emailConnection() });
    },
  });
}

export function useDisconnectEmailInbox() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectEmailInbox({ getToken }),
    onSuccess: () => {
      // Remove cached data immediately so the UI transitions to "not connected"
      // without waiting for a background refetch to complete (RQ v5 keeps stale
      // data visible during refetch, causing a delayed UI update).
      qc.removeQueries({ queryKey: settingsKeys.emailConnection() });
    },
  });
}
